<section
    class="booking-details-step-card"
    appVenueColour
    [primaryColour]="venue().primaryColour"
    aria-labelledby="booking-details-heading"
>
    <div class="booking-details-step-header">
        <h2
            #heading
            id="booking-details-heading"
            class="booking-step-heading h4 m-0"
            tabindex="-1"
        >
            Your Details
        </h2>
    </div>

    <form
        #detailsForm
        class="booking-details-content vstack"
        [formGroup]="form"
        (ngSubmit)="submit()"
        novalidate
    >
        @if (errorMessage(); as error) {
            <div
                class="alert alert-danger mb-0"
                role="alert"
                data-testid="booking-details-request-error"
            >
                {{ error }}
            </div>
        }

        <section
            class="booking-details-section vstack gap-4"
            aria-labelledby="contact-details-heading"
        >
            <div class="booking-details-paired-fields">
                <div>
                    <label class="form-label" for="booking-first-name">
                        First Name
                    </label>
                    <input
                        id="booking-first-name"
                        class="form-control"
                        type="text"
                        formControlName="firstName"
                        placeholder="Enter name"
                        autocomplete="given-name"
                        autocapitalize="words"
                        appCapitaliseOnBlur
                        required
                        aria-required="true"
                        [attr.aria-invalid]="isInvalid(form.controls.firstName)"
                        [attr.aria-describedby]="isInvalid(form.controls.firstName)
                            ? 'booking-first-name-error'
                            : null"
                        [class.is-invalid]="isInvalid(form.controls.firstName)"
                        data-testid="first-name"
                    />
                    @if (isInvalid(form.controls.firstName)) {
                        <div id="booking-first-name-error" class="invalid-feedback">
                            Enter your first name.
                        </div>
                    }
                </div>

                <div>
                    <label class="form-label" for="booking-last-name">
                        Surname
                    </label>
                    <input
                        id="booking-last-name"
                        class="form-control"
                        type="text"
                        formControlName="lastName"
                        placeholder="Enter surname"
                        autocomplete="family-name"
                        autocapitalize="words"
                        appCapitaliseOnBlur
                        required
                        aria-required="true"
                        [attr.aria-invalid]="isInvalid(form.controls.lastName)"
                        [attr.aria-describedby]="isInvalid(form.controls.lastName)
                            ? 'booking-last-name-error'
                            : null"
                        [class.is-invalid]="isInvalid(form.controls.lastName)"
                        data-testid="last-name"
                    />
                    @if (isInvalid(form.controls.lastName)) {
                        <div id="booking-last-name-error" class="invalid-feedback">
                            Enter your surname.
                        </div>
                    }
                </div>
            </div>

            <div class="booking-details-paired-fields booking-details-contact-fields">
                <div>
                    <label class="form-label" for="booking-phone-number">
                        Phone Number
                        @if (!venue().phoneNumberRequired) {
                            <span class="text-secondary fw-normal">(optional)</span>
                        }
                    </label>
                    <div [class.booking-details-control-invalid]="isInvalid(form.controls.phoneNumber)">
                        <ngx-intl-tel-input
                            appBookingPhoneAccessibility
                            cssClass="form-control"
                            [preferredCountries]="[phoneNumberCountryCode()]"
                            [enableAutoCountrySelect]="true"
                            [enablePlaceholder]="true"
                            customPlaceholder="Enter phone number"
                            [searchCountryFlag]="true"
                            [searchCountryField]="[SearchCountryField.Iso2, SearchCountryField.Name]"
                            [selectFirstCountry]="false"
                            [selectedCountryISO]="phoneNumberCountryCode()"
                            [separateDialCode]="false"
                            [maxLength]="15"
                            [numberFormat]="PhoneNumberFormat.International"
                            [phoneValidation]="true"
                            inputId="booking-phone-number"
                            name="phone"
                            formControlName="phoneNumber"
                            [bookingPhoneRequired]="venue().phoneNumberRequired"
                            [bookingPhoneInvalid]="isInvalid(form.controls.phoneNumber)"
                            [bookingPhoneDescribedBy]="isInvalid(form.controls.phoneNumber)
                                ? 'booking-phone-number-error'
                                : null"
                            data-testid="phone-number"
                        ></ngx-intl-tel-input>
                    </div>
                    @if (isInvalid(form.controls.phoneNumber)) {
                        <div id="booking-phone-number-error" class="invalid-feedback d-block">
                            {{ venue().phoneNumberRequired
                                ? 'Enter a valid phone number.'
                                : 'Check that the phone number is valid.' }}
                        </div>
                    }
                </div>

                <div>
                    <label class="form-label" for="booking-email-address">
                        Email Address
                    </label>
                    <input
                        id="booking-email-address"
                        class="form-control"
                        type="email"
                        formControlName="emailAddress"
                        placeholder="Enter email address"
                        autocomplete="email"
                        required
                        aria-required="true"
                        [attr.aria-invalid]="isInvalid(form.controls.emailAddress)"
                        [attr.aria-describedby]="isInvalid(form.controls.emailAddress)
                            ? 'booking-email-address-error'
                            : null"
                        [class.is-invalid]="isInvalid(form.controls.emailAddress)"
                        data-testid="email-address"
                    />
                    @if (form.controls.emailAddress.hasError('serverValidation')) {
                        <div id="booking-email-address-error" class="invalid-feedback d-block">
                            We couldn't verify this email address. Double-check it for typos or try another address.
                        </div>
                    } @else if (isInvalid(form.controls.emailAddress)) {
                        <div id="booking-email-address-error" class="invalid-feedback">
                            Enter a valid email address.
                        </div>
                    }
                </div>
            </div>

            <div>
                <label class="form-label" for="booking-notes">
                    Additional Notes <span class="text-secondary fw-normal">(optional)</span>
                </label>
                <textarea
                    id="booking-notes"
                    class="form-control"
                    rows="3"
                    formControlName="notes"
                    placeholder="Enter any additional notes or special requests..."
                    data-testid="notes"
                ></textarea>
            </div>
        </section>

        <section
            class="booking-details-section booking-agreements vstack gap-2"
            aria-labelledby="booking-agreements-heading"
        >
            <h3 id="booking-agreements-heading" class="visually-hidden">
                Booking questions and agreements
            </h3>
            <ng-template
                #checkmarkRow
                let-control="control"
                let-inputId="inputId"
                let-labelTemplate="labelTemplate"
                let-required="required"
                let-invalid="invalid"
                let-describedby="describedby"
                let-dataTestId="dataTestId"
            >
                <div class="form-check hstack gap-3 align-items-center">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        [id]="inputId"
                        [formControl]="control"
                        [required]="required"
                        [attr.aria-required]="required ? 'true' : null"
                        [attr.aria-invalid]="invalid"
                        [attr.aria-describedby]="describedby"
                        [class.is-invalid]="invalid"
                        [attr.data-testid]="dataTestId"
                    />
                    <label class="form-check-label" [for]="inputId">
                        <ng-container [ngTemplateOutlet]="labelTemplate"></ng-container>
                    </label>
                </div>
            </ng-template>
            @if (questions.controls.length > 0) {
                <div formArrayName="questions" class="booking-questions vstack gap-2">
                    @for (questionControl of questions.controls; track questionControl; let index = $index) {
                        <div [formGroupName]="index" class="booking-question">
                            @let question = questionControl.controls.question.value;

                            @if (question.type === QuestionType.Checkbox) {
                                <ng-template #checkboxQuestionLabel>
                                    {{ questionLabel(question) }}
                                    @if (question.required) {
                                        <span class="visually-hidden"> (required)</span>
                                    }
                                </ng-template>
                                <ng-container
                                    [ngTemplateOutlet]="checkmarkRow"
                                    [ngTemplateOutletContext]="{
                                        control: questionControl.controls.answer,
                                        inputId: 'booking-question-' + index,
                                        labelTemplate: checkboxQuestionLabel,
                                        required: question.required,
                                        invalid: questionIsInvalid(questionControl),
                                        describedby: questionIsInvalid(questionControl)
                                            ? 'booking-question-' + index + '-error'
                                            : null
                                    }"
                                ></ng-container>
                            } @else if (question.type === QuestionType.AcknowledgeEndTime) {
                                <ng-template #endTimeQuestionLabel>
                                    I acknowledge that my booking ends at
                                    <strong>
                                        <time [attr.datetime]="bookingEndTime().toISOString()">
                                            {{ bookingEndTime() | date:'shortTime' }}
                                        </time>
                                    </strong>
                                    @if (question.required) {
                                        <span class="visually-hidden"> (required)</span>
                                    }
                                </ng-template>
                                <ng-container
                                    [ngTemplateOutlet]="checkmarkRow"
                                    [ngTemplateOutletContext]="{
                                        control: questionControl.controls.answer,
                                        inputId: 'booking-question-' + index,
                                        labelTemplate: endTimeQuestionLabel,
                                        required: question.required,
                                        invalid: questionIsInvalid(questionControl),
                                        describedby: questionIsInvalid(questionControl)
                                            ? 'booking-question-' + index + '-error'
                                            : null
                                    }"
                                ></ng-container>
                            } @else if (question.type === QuestionType.Dropdown
                                || question.type === QuestionType.ReasonsForVisit) {
                                <label
                                    class="form-label"
                                    [for]="'booking-question-' + index"
                                >
                                    {{ questionLabel(question) }}
                                    @if (!question.required) {
                                        <span class="text-secondary fw-normal">(optional)</span>
                                    }
                                </label>
                                <select
                                    class="form-select"
                                    [id]="'booking-question-' + index"
                                    formControlName="answer"
                                    [required]="question.required"
                                    [attr.aria-required]="question.required"
                                    [attr.aria-invalid]="questionIsInvalid(questionControl)"
                                    [attr.aria-describedby]="questionIsInvalid(questionControl)
                                        ? 'booking-question-' + index + '-error'
                                        : null"
                                    [class.is-invalid]="questionIsInvalid(questionControl)"
                                >
                                    <option [ngValue]="null">
                                        {{ question.required ? 'Select an option' : 'No selection' }}
                                    </option>
                                    @for (option of question.options; track option) {
                                        <option [value]="option">{{ option }}</option>
                                    }
                                </select>
                            } @else if (question.type === QuestionType.DOB) {
                                <div
                                    class="booking-dob-question d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center justify-content-between gap-3"
                                    role="group"
                                    [attr.aria-labelledby]="'booking-question-' + index + '-label'"
                                    [attr.aria-required]="question.required"
                                    [attr.aria-invalid]="questionIsInvalid(questionControl)"
                                    [attr.aria-describedby]="questionIsInvalid(questionControl)
                                        ? 'booking-question-' + index + '-error'
                                        : null"
                                >
                                    <span
                                        class="form-label mb-0"
                                        [id]="'booking-question-' + index + '-label'"
                                    >
                                        {{ questionLabel(question) || 'When is your birthday?' }}
                                        @if (!question.required) {
                                            <span class="text-secondary fw-normal">(optional)</span>
                                        }
                                    </span>
                                    <div class="booking-dob-fields d-flex gap-3 w-100">
                                        <label class="visually-hidden" [for]="'booking-question-month-' + index">
                                            Month
                                        </label>
                                        <select
                                            class="form-select"
                                            [id]="'booking-question-month-' + index"
                                            [value]="dobMonth(questionControl)"
                                            [disabled]="form.disabled"
                                            [required]="question.required"
                                            [attr.aria-required]="question.required"
                                            [attr.aria-invalid]="questionIsInvalid(questionControl)"
                                            [attr.aria-describedby]="questionIsInvalid(questionControl)
                                                ? 'booking-question-' + index + '-error'
                                                : null"
                                            [class.is-invalid]="questionIsInvalid(questionControl)"
                                            (change)="updateDob(questionControl, 'month', $event)"
                                        >
                                            <option value="">Month</option>
                                            @for (month of months; track $index) {
                                                <option [value]="($index + 1).toString().padStart(2, '0')">
                                                    {{ month }}
                                                </option>
                                            }
                                        </select>
                                        <label class="visually-hidden" [for]="'booking-question-day-' + index">
                                            Day
                                        </label>
                                        <select
                                            class="form-select"
                                            [id]="'booking-question-day-' + index"
                                            [value]="dobDay(questionControl)"
                                            [disabled]="!dobMonth(questionControl) || form.disabled"
                                            [required]="question.required"
                                            [attr.aria-required]="question.required"
                                            [attr.aria-invalid]="questionIsInvalid(questionControl)"
                                            [attr.aria-describedby]="questionIsInvalid(questionControl)
                                                ? 'booking-question-' + index + '-error'
                                                : null"
                                            [class.is-invalid]="questionIsInvalid(questionControl)"
                                            (change)="updateDob(questionControl, 'day', $event)"
                                        >
                                            <option value="">Day</option>
                                            @for (day of days; track day) {
                                                <option [value]="day.toString().padStart(2, '0')">{{ day }}</option>
                                            }
                                        </select>
                                    </div>
                                </div>
                            } @else if (question.type === QuestionType.DietaryRestrictions) {
                                <div>
                                    <label
                                        class="form-label"
                                        [for]="'booking-question-' + index"
                                    >
                                        {{ questionLabel(question) }}
                                        @if (!question.required) {
                                            <span class="text-secondary fw-normal">(optional)</span>
                                        }
                                    </label>
                                    <div
                                        ngbDropdown
                                        container="body"
                                        #dietaryDropdown="ngbDropdown"
                                        dropdownClass="dropdown-md-popup"
                                        placement="top bottom end auto"
                                    >
                                        @let answer = dietarySelections(questionControl);
                                        <button
                                            type="button"
                                            class="booking-dietary-toggle btn btn-outline-secondary text-start w-100 d-flex align-items-center gap-2"
                                            [id]="'booking-question-' + index"
                                            [attr.data-testid]="'booking-question-' + index + '-button'"
                                            ngbDropdownToggle
                                            [class.is-invalid]="questionIsInvalid(questionControl)"
                                            [attr.aria-required]="question.required"
                                            [attr.aria-invalid]="questionIsInvalid(questionControl)"
                                            [attr.aria-describedby]="questionIsInvalid(questionControl)
                                                ? 'booking-question-' + index + '-error'
                                                : null"
                                            [disabled]="form.disabled"
                                        >
                                            <span class="text-truncate flex-grow-1">
                                                @if (!answer.length && !question.required) {
                                                    No dietary restrictions
                                                } @else if (!answer.length) {
                                                    Let us know your dietary restrictions
                                                } @else {
                                                    {{ answer.slice(0, 3).join(', ') }}
                                                }
                                            </span>
                                            @if (answer.length > 3) {
                                                <span class="flex-shrink-0">
                                                    + {{ answer.length - 3 }} more
                                                </span>
                                            }
                                        </button>
                                        <div ngbDropdownMenu class="border-0 p-0 shadow-lg">
                                            <app-multiple-choice-input
                                                [venue]="venue()"
                                                [options]="question.dietaryRestrictionOptions"
                                                title="Select dietary restrictions"
                                                [formControl]="$any(questionControl.controls.answer)"
                                                (closeRequested)="dietaryDropdown.close()"
                                            ></app-multiple-choice-input>
                                        </div>
                                    </div>
                                </div>
                            } @else {
                                <label
                                    class="form-label"
                                    [for]="'booking-question-' + index"
                                >
                                    {{ questionLabel(question) }}
                                    @if (!question.required) {
                                        <span class="text-secondary fw-normal">(optional)</span>
                                    }
                                </label>
                                <input
                                    class="form-control"
                                    type="text"
                                    [id]="'booking-question-' + index"
                                    formControlName="answer"
                                    [required]="question.required"
                                    [attr.aria-required]="question.required"
                                    [attr.aria-invalid]="questionIsInvalid(questionControl)"
                                    [attr.aria-describedby]="questionIsInvalid(questionControl)
                                        ? 'booking-question-' + index + '-error'
                                        : null"
                                    [class.is-invalid]="questionIsInvalid(questionControl)"
                                />
                            }

                            @if (questionIsInvalid(questionControl)) {
                                <div
                                    [id]="'booking-question-' + index + '-error'"
                                    class="invalid-feedback d-block"
                                >
                                    @if (questionControl.controls.answer.hasError('dayRequired')) {
                                        Select a day.
                                    } @else if (questionControl.controls.answer.hasError('invalidDate')) {
                                        Select a valid date.
                                    } @else {
                                        This question is required.
                                    }
                                </div>
                            }
                        </div>
                    }
                </div>
            }

            @if (merchantPrivacyPolicyPath(); as merchantPolicyPath) {
                <div>
                    <ng-template #merchantPrivacyLabel>
                        I have read and accept the {{ venue().displayName }}
                        <a
                            class="text-body"
                            [href]="merchantPolicyPath"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="merchant-privacy-policy-link"
                        >Privacy Policy</a> agreement
                    </ng-template>
                    <ng-container
                        [ngTemplateOutlet]="checkmarkRow"
                        [ngTemplateOutletContext]="{
                            control: form.controls.agreedToMerchantPrivacy,
                            inputId: 'booking-agreed-to-merchant-privacy',
                            labelTemplate: merchantPrivacyLabel,
                            required: true,
                            invalid: isInvalid(form.controls.agreedToMerchantPrivacy),
                            describedby: isInvalid(form.controls.agreedToMerchantPrivacy)
                                ? 'booking-agreed-to-merchant-privacy-error'
                                : null,
                            dataTestId: 'agreed-to-merchant-privacy'
                        }"
                    ></ng-container>
                    @if (isInvalid(form.controls.agreedToMerchantPrivacy)) {
                        <div id="booking-agreed-to-merchant-privacy-error" class="invalid-feedback d-block">
                            Accept the venue's Privacy Policy to continue with your booking.
                        </div>
                    }
                </div>
            }
            <div>
                <ng-template #termsLabel>
                    I have read and accept the TableSense
                    <a
                        class="text-body"
                        [href]="privacyPolicyURL"
                        target="_blank"
                        rel="noopener"
                    >Privacy Policy</a> agreement
                </ng-template>
                <ng-container
                    [ngTemplateOutlet]="checkmarkRow"
                    [ngTemplateOutletContext]="{
                        control: form.controls.agreedToTerms,
                        inputId: 'booking-agreed-to-terms',
                        labelTemplate: termsLabel,
                        required: true,
                        invalid: isInvalid(form.controls.agreedToTerms),
                        describedby: isInvalid(form.controls.agreedToTerms)
                            ? 'booking-agreed-to-terms-error'
                            : null,
                        dataTestId: 'agreed-to-terms'
                    }"
                ></ng-container>
                @if (isInvalid(form.controls.agreedToTerms)) {
                    <div id="booking-agreed-to-terms-error" class="invalid-feedback d-block">
                        Accept the Privacy Policy to continue with your booking.
                    </div>
                }
            </div>
            <div>
                <ng-template #marketingLabel>
                    I would like to receive news and offers from {{ venue().displayName }}
                </ng-template>
                <ng-container
                    [ngTemplateOutlet]="checkmarkRow"
                    [ngTemplateOutletContext]="{
                        control: form.controls.agreedToMarketing,
                        inputId: 'booking-agreed-to-marketing',
                        labelTemplate: marketingLabel,
                        required: false,
                        invalid: false,
                        describedby: null
                    }"
                ></ng-container>
            </div>
        </section>

        @if (showPaymentSection()) {
            <section
                class="booking-details-section booking-payment-section vstack gap-4"
                [class.booking-payment-section-active]="formValid()"
                aria-labelledby="payment-requirement-heading"
                [attr.aria-busy]="isPreparingPayment()"
                data-testid="booking-payment-requirement"
            >
                @if (upsellItems().length > 0) {
                    <div class="vstack gap-2 d-lg-none" data-testid="mobile-upsell-summary">
                        <h3 class="h6 fw-semibold mb-0">Your products</h3>
                        @for (item of upsellItems(); track $index) {
                            <div class="d-flex justify-content-between gap-3">
                                <span>{{ item.quantity }} &times; {{ item.name }}
                                    @if (item.unitAmount !== undefined) {
                                        <small class="d-block text-secondary">{{ null | currencyUnit : item.unitAmount : venue().currencyCode }} each</small>
                                        <app-booking-product-refund-policy [policy]="item" />
                                    }
                                </span>
                                @if (item.unitAmount !== undefined) {
                                    <span>{{ null | currencyUnit : item.unitAmount * item.quantity : venue().currencyCode }}</span>
                                }
                            </div>
                        }
                    </div>
                }
                @if (!paymentRequirementResolved()) {
                    <div
                        class="booking-payment-disclosure booking-payment-disclosure-copy px-3 py-2 border rounded-3"
                        data-testid="booking-payment-disclosure"
                    >
                        <p
                            id="payment-requirement-heading"
                            class="booking-payment-disclosure-title"
                        >
                            Payment details
                        </p>
                        <p class="mb-0">
                            Payment or card details may be required to secure your booking.
                        </p>
                    </div>
                } @else {
                    @if (paymentRequirement(); as payment) {
                        <div
                            class="booking-payment-disclosure d-flex align-items-start gap-3 px-3 py-2 border rounded-3"
                            data-testid="booking-payment-disclosure"
                        >
                            <div class="booking-payment-disclosure-copy">
                                <p
                                    id="payment-requirement-heading"
                                    class="booking-payment-disclosure-title"
                                >
                                    {{ payment.kind === 'deposit'
                                        ? paymentTerminology().label + ' required'
                                        : 'Card details required' }}
                                </p>
                                @if (payment.kind === 'deposit') {
                                    <p class="mb-0">
                                        @if (payment.orderAmount) {
                                            Pay <strong>{{ null | currencyUnit:payment.amount:venue().currencyCode }}</strong>
                                            for your products{{ (payment.depositAmount ?? 0) > 0 ? ' and booking deposit' : '' }}.
                                        } @else {
                                            We require a <strong>{{ null | currencyUnit:payment.amount:venue().currencyCode }}</strong>
                                            {{ paymentTerminology().lowercaseLabel }} to secure your booking.
                                        }
                                    </p>
                                    @if (payment.orderAmount) {
                                        <p class="small text-secondary mt-2 mb-0">Each product’s refund terms are shown with your selection. Only eligible products are refunded when you cancel before the deadline.</p>
                                    }
                                    @if ((payment.depositAmount ?? payment.amount) > 0 && venue().depositRefundPolicy) {
                                        <quill-view
                                            class="booking-payment-policy d-block mt-3"
                                            [content]="venue().depositRefundPolicy"
                                        ></quill-view>
                                    } @else if ((payment.depositAmount ?? payment.amount) > 0 && payment.refundCutOffHours !== null) {
                                        <p class="mb-0 mt-2">
                                            Cancellations or no-shows with less than
                                            {{ payment.refundCutOffHours | i18nPlural : hoursPluralMapping }}
                                            notice will not be refunded.
                                        </p>
                                    } @else if ((payment.depositAmount ?? payment.amount) > 0) {
                                        <p class="mb-0 mt-2">
                                            @if (payment.orderAmount) { The deposit is not refundable for cancellations or no-shows. }
                                            @else { Cancellations or no-shows will not be refunded. }
                                        </p>
                                    }
                                } @else {
                                    <p class="mb-0">
                                        A card is required to secure your booking. Cancellations or no-shows
                                        @if (payment.cutOffHours !== null) {
                                            with less than
                                            {{ payment.cutOffHours | i18nPlural : hoursPluralMapping }}
                                            notice
                                        }
                                        may be charged
                                        <strong>
                                            {{ null | currencyUnit : payment.amount : venue().currencyCode }}
                                            @if (payment.isPerCover) {
                                                per person
                                            }
                                        </strong>.
                                    </p>
                                }
                                @if (payment.kind === 'card' && payment.orderAmount) {
                                    <p class="mb-0 mt-2">
                                        @if (venue().supportsCardSetupWithBookingPayment()) {
                                            Pay <strong>{{ null | currencyUnit:payment.orderAmount:venue().currencyCode }}</strong> for your products when you confirm your booking. Your card will also secure the booking.
                                        } @else {
                                            After saving your card, pay {{ null | currencyUnit:payment.orderAmount:venue().currencyCode }} for your products now or later through your booking link.
                                        }
                                        This is separate from any later cancellation fee.
                                    </p>
                                }
                            </div>
                        </div>
                    }
                }

                @if (isPreparingPayment()) {
                    <div
                        class="booking-payment-preparing d-flex align-items-center justify-content-center gap-3"
                        role="status"
                        data-testid="booking-payment-preparing"
                    >
                        <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
                        <span>Preparing secure payment details&hellip;</span>
                    </div>
                }

                @if (paymentPreparationFailed()) {
                    <button
                        type="button"
                        class="btn btn-outline-primary align-self-start"
                        (click)="requestPaymentPreparation()"
                        data-testid="retry-payment-preparation"
                    >
                        Try loading payment again
                    </button>
                }

                @if (paymentIntent() === null && !isPreparingPayment()) {
                    <app-venue-storefront-booking-payment-placeholder />
                }

                @if (paymentIntent(); as intent) {
                    @if (paymentUsage(); as usage) {
                        <div
                            class="booking-payment-entry vstack gap-3 pt-3 border-top"
                            aria-labelledby="booking-payment-entry-heading"
                            data-testid="booking-payment-entry"
                        >
                            <h4
                                id="booking-payment-entry-heading"
                                class="h6 fw-semibold m-0"
                            >
                                {{ usage === 'payment' ? 'Pay securely' : 'Secure your booking' }}
                            </h4>
                            <div
                                class="booking-payment-form-stage"
                                [class.booking-payment-form-stage-consent]="usage === 'setup-card' && venue().paymentsAccountForCapability('CancellationChargePayments')?.gateway === 'dojo'"
                            >
                                <app-venue-storefront-booking-payment-element
                                    [venue]="venue()"
                                    [intent]="intent"
                                    [usage]="usage"
                                    [saveCardWithPayment]="usage === 'payment' && paymentRequirement()?.kind === 'card'"
                                    [billingEmailAddress]="form.controls.emailAddress.value"
                                    [futureChargeAmount]="futureChargeAmount()"
                                    [currencyCode]="venue().currencyCode"
                                    (statusChange)="paymentStatusChange.emit($event)"
                                    (readyChange)="paymentReady.set($event)"
                                    (confirmingChange)="paymentConfirmingChanged($event)"
                                    (confirmed)="paymentConfirmedFromElement($event)"
                                ></app-venue-storefront-booking-payment-element>
                            </div>
                        </div>
                    }
                }

                @if (paymentRequirement(); as payment) {
                    @if (paymentRequirementResolved() && payment.kind === 'deposit') {
                        <aside
                            class="booking-fees-card p-3 border rounded-3"
                            aria-labelledby="booking-fees-heading"
                            data-testid="booking-fees-card"
                        >
                            <h4
                                id="booking-fees-heading"
                                class="h6 fw-semibold mb-3"
                            >
                                Fees &amp; Add-ons
                            </h4>
                            <dl class="vstack gap-3 mb-0">
                                @if ((payment.depositAmount ?? payment.amount) > 0) {
                                    <div class="d-flex justify-content-between gap-3">
                                        <dt class="fw-normal">
                                            {{ payment.orderAmount ? 'Deposit' : paymentTerminology().label }}
                                            @if (payment.perCoverAmount !== null) {
                                                ({{ partySize() }} x {{ null | currencyUnit:payment.perCoverAmount:venue().currencyCode }})
                                            }
                                        </dt>
                                        <dd class="mb-0">{{ null | currencyUnit:payment.depositAmount ?? payment.amount:venue().currencyCode }}</dd>
                                    </div>
                                }
                                @if (payment.orderAmount) {
                                    <div class="d-flex justify-content-between gap-3">
                                        <dt class="fw-normal">Products</dt><dd class="mb-0">{{ null | currencyUnit:payment.orderAmount:venue().currencyCode }}</dd>
                                    </div>
                                }
                                <div class="d-flex justify-content-between gap-3 pt-3 border-top fw-semibold">
                                    <dt>Total to Pay</dt>
                                    <dd class="mb-0">
                                        {{ null | currencyUnit : payment.amount : venue().currencyCode }}
                                    </dd>
                                </div>
                            </dl>
                        </aside>
                    }
                }
            </section>
        }

        <button
            type="submit"
            class="btn btn-primary booking-details-submit d-flex align-items-center justify-content-center gap-2"
            [disabled]="submitDisabled()"
            data-testid="confirm-booking-details"
        >
            @if (isSubmitting() || paymentConfirming()) {
                <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
            }
            {{ submitLabel() }}
        </button>
    </form>
</section>
